import { Form, Input } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useUpdateSignRequestMutation } from '../../api/sign-requests-api';
import { signRequestAlertId, signRequestPreviewUpdateModalId } from '../../constants/constants';
import { selectTempSignRequestId, setTempSignRequestId } from '../../store/sign-requests-slice';

interface Props {
  formId: string;
  inputsData: Record<
    string,
    {
      label: string;
      placeholder: string;
      initialValue: string;
    }
  >;
}

export type PreviewUpdateFormType = {
  [key: string]: string;
};

const SignRequestsPreviewUpdateContent = ({
  isOpen,
  afterClose,
  inputsData,
  formId,
}: LazyModalComponentProps & Props): JSX.Element => {
  const [form] = Form.useForm<PreviewUpdateFormType>();
  const { t } = useTranslation(['sign-requests', 'common']);
  const dispatch = useDispatch();
  const activeSignRequestId = useAppSelector(selectTempSignRequestId);

  const [updateSignRequest, { isLoading }] = useUpdateSignRequestMutation();

  const initialValues: PreviewUpdateFormType = useMemo(() => {
    return Object.keys(inputsData)
      .map(key => ({
        [key]: inputsData[key].initialValue,
      }))
      .reduce((accumulator, object) => ({ ...accumulator, ...object }), {});
  }, [inputsData]);

  const closePreviewModal = () => {
    dispatch(closeModal(signRequestPreviewUpdateModalId));
    dispatch(setTempSignRequestId(null));
  };

  const submitPreviewUpdateForm = (formValues: PreviewUpdateFormType) => {
    if (!activeSignRequestId || !formValues.name) {
      return;
    }
    updateSignRequest({
      name: formValues.name,
      id: activeSignRequestId,
    })
      .unwrap()
      .then(() => {
        closePreviewModal();
        dispatch(
          setAlert({
            id: signRequestAlertId,
            type: 'success',
            title: t('signRequestsPreview.message.signRequestUpdated'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(
          form,
          error.data?.errors || {
            [Object.keys(inputsData)[0]]: [t('signScanRequest.validationError.requestRequired', { ns: 'common' })],
          },
        );
      });
  };

  return (
    <Modal
      title={t('signRequestsPreview.title')}
      onCancel={() => !isLoading && closePreviewModal()}
      isLoading={isLoading}
      isOpen={isOpen}
      afterClose={afterClose}
      formId={formId}
    >
      <Form id={formId} form={form} initialValues={initialValues} layout="vertical" onFinish={submitPreviewUpdateForm}>
        {Object.entries(inputsData).map(inputValue => {
          return (
            <Form.Item key={inputValue[0]} name={inputValue[0]} label={inputValue[1].label}>
              <Input placeholder={inputValue[1].placeholder} />
            </Form.Item>
          );
        })}
      </Form>
    </Modal>
  );
};

const SignRequestsPreviewUpdateModal = (modalProps: Props): JSX.Element => {
  return (
    <LazyModal
      modalId={modalProps.formId}
      Component={props => <SignRequestsPreviewUpdateContent {...props} {...modalProps} />}
    />
  );
};
export default SignRequestsPreviewUpdateModal;
